import { useState } from 'react';

import { HStack, useColorModeValue, useToast } from '@ecoinvent/ui';
import { BiCopy } from 'react-icons/bi';
import { FiChevronRight } from 'react-icons/fi';

import { COLORS, SUPPLY_CARD_TEXT_INDENT } from './constants';
import { stopEvents as stopAllEvents } from './utils';

const secondaryButtonStyle = (borderColor: string, bgColor: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '32px',
  padding: '0 10px',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  fontWeight: 600,
  borderRadius: '6px',
  border: `1px solid ${borderColor}`,
  background: bgColor,
  cursor: 'pointer',
  justifyContent: 'center',
  whiteSpace: 'nowrap' as const,
});

type Props = {
  onDocumentation: () => void;
  onCopyScore: () => Promise<void>;
  isCopyDisabled: boolean;
};

const ImpactAssessmentButtons = ({ onDocumentation, onCopyScore, isCopyDisabled }: Props) => {
  const [isCopying, setIsCopying] = useState(false);
  const toast = useToast();
  const borderColor = useColorModeValue(COLORS.borderLight, COLORS.buttonOutlinedBorderDark);
  const textColor = useColorModeValue(COLORS.textLight, COLORS.textDark);
  const bgColor = useColorModeValue(COLORS.secondaryCtaBgLight, COLORS.darkSurface);

  const handleCopy = async (e: React.MouseEvent) => {
    stopAllEvents(e);
    if (isCopying || isCopyDisabled) return;
    setIsCopying(true);
    try {
      await onCopyScore();
    } catch {
      // `navigator.clipboard.writeText` rejects when the document is unfocused or permission is denied.
      toast({
        title: 'Oh no...',
        description: 'There was an error copying this score. Please try again later.',
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <HStack spacing={2} pl={`${SUPPLY_CARD_TEXT_INDENT}px`} pt={1}>
      <button
        type="button"
        className="nopan nodrag"
        style={{
          color: textColor,
          fontSize: '0.875rem',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          background: 'none',
          border: 'none',
          fontFamily: 'inherit',
          padding: 0,
        }}
        onPointerDown={stopAllEvents}
        onMouseDown={stopAllEvents}
        onClick={(e) => {
          stopAllEvents(e);
          onDocumentation();
        }}
      >
        View detail <FiChevronRight size={14} />
      </button>
      <button
        type="button"
        className="nopan nodrag"
        // Disabled with nothing to copy: the click is otherwise a silent no-op.
        disabled={isCopyDisabled}
        style={{
          ...secondaryButtonStyle(borderColor, bgColor),
          color: textColor,
          opacity: isCopying || isCopyDisabled ? 0.6 : 1,
          cursor: isCopyDisabled ? 'not-allowed' : 'pointer',
        }}
        onPointerDown={stopAllEvents}
        onMouseDown={stopAllEvents}
        onClick={handleCopy}
      >
        <BiCopy size={13} />
        {isCopying ? 'Copying…' : 'Copy Score'}
      </button>
    </HStack>
  );
};

export default ImpactAssessmentButtons;
